// components/DeploymentsView.tsx
import React from 'react';
import { useAuth } from '../context/AuthContext';

interface DeploymentsViewProps {
  onBack: () => void;
}

const DeploymentsView: React.FC<DeploymentsViewProps> = ({ onBack }) => {
  const { deployments, logout } = useAuth();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f5f5 0%, #e5e7eb 100%)",
      padding: '20px',
      fontFamily: "Inter, sans-serif",
    }}>
      
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '32px',
        background: 'white',
        padding: '20px 32px',
        borderRadius: '16px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={onBack}
            style={{
              background: '#f3f4f6',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151'
            }}
          >
            ← Back to Dashboard
          </button>
          <div>
            <h1 style={{ color: '#2563eb', fontSize: '28px', fontWeight: 800, margin: 0 }}>
              Your Deployments
            </h1>
            <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0 0' }}>
              Manage and view all your deployed sites
            </p>
          </div>
        </div>
        <button 
          onClick={logout}
          style={{
            padding: '12px 20px',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Logout
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '20px', 
        marginBottom: '32px' 
      }}>
        <div style={{
          background: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 800, color: '#3b82f6', marginBottom: '8px' }}>
            {deployments.length}
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: 600 }}>
            Total Deployments
          </div>
        </div>
        
        <div style={{
          background: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 800, color: '#10b981', marginBottom: '8px' }}>
            {deployments.length}
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: 600 }}>
            Active Sites
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 800, color: '#f59e0b', marginBottom: '8px' }}>
            {deployments.length > 0 ? formatDate(deployments[0].created_at).split(',')[0] : 'N/A'}
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: 600 }}>
            Latest Deployment
          </div>
        </div>
      </div>

      {/* Deployments Table */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden'
      }}>
        
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #e5e7eb',
          background: '#f9fafb'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: 700, 
            color: '#374151' 
          }}>
            Deployment History
          </h3>
        </div>

        {deployments.length === 0 ? (
          <div style={{
            padding: '64px 32px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📭</div>
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '18px', 
              fontWeight: 600, 
              margin: '0 0 8px 0' 
            }}>
              No deployments yet
            </h3>
            <p style={{ 
              color: '#9ca3af', 
              fontSize: '14px', 
              margin: 0 
            }}>
              Create your first deployment to see it here
            </p>
            <button
              onClick={onBack}
              style={{
                marginTop: '24px',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Create Your First Deployment
            </button>
          </div>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ 
                    padding: '16px 24px', 
                    textAlign: 'left', 
                    fontWeight: 700, 
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    Site
                  </th>
                  <th style={{ 
                    padding: '16px 24px', 
                    textAlign: 'left', 
                    fontWeight: 700, 
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    URL
                  </th>
                  <th style={{ 
                    padding: '16px 24px', 
                    textAlign: 'left', 
                    fontWeight: 700, 
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    Deployed
                  </th>
                  <th style={{ 
                    padding: '16px 24px', 
                    textAlign: 'left', 
                    fontWeight: 700, 
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    Status
                  </th>
                  <th style={{ 
                    padding: '16px 24px', 
                    textAlign: 'center', 
                    fontWeight: 700, 
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {deployments.map((deployment, index) => (
                  <tr 
                    key={deployment.id}
                    style={{ 
                      borderBottom: index !== deployments.length - 1 ? '1px solid #f3f4f6' : 'none',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px'
                        }}>
                          🌐
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: '#374151' }}>
                            {deployment.subdomain}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            pionedata.com
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <a 
                        href={deployment.https_url} 
                        target="_blank" 
                        rel="noreferrer"
                        style={{ 
                          color: '#3b82f6', 
                          textDecoration: 'none',
                          fontWeight: 600,
                          fontSize: '13px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        {deployment.https_url}
                      </a>
                    </td>
                    <td style={{ padding: '16px 24px', color: '#6b7280' }}>
                      {formatDate(deployment.created_at)}
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <span style={{
                        padding: '4px 8px',
                        background: '#dcfce7',
                        color: '#16a34a',
                        fontSize: '12px',
                        fontWeight: 600,
                        borderRadius: '6px'
                      }}>
                        Live
                      </span>
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'center' }}>
                      <a 
                        href={deployment.https_url} 
                        target="_blank" 
                        rel="noreferrer"
                        style={{
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                      >
                        Visit Site
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeploymentsView;